import { MdPhone } from "react-icons/md";
import { ErrorBoundary } from "@sentry/react";
import { useContext, useEffect, useState } from "react";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";

import { api } from "~/data/core/api";
import { socket } from "~/data/core/socket";

import { InputSearch } from "../shared/input";
import { UserAvatar } from "../shared/user-avatar";
import { useUser } from "../shared/queries/user-query";
import { ErrorFallback } from "../shared/error-fallback";
import { CallContext } from "../shared/contexts/call-context";

export function OnlineUsersSection() {
  const [search, setSearch] = useState("");
  return (
    <section className="rounded-[20px] bg-white shadow-card p-5 pb-[10px]">
      <div className="flex items-center gap-3 mb-[10px]">
        <h2 className="font-alternative text-2xl font-semibold uppercase">
          Colegas Online
        </h2>
        <span className="py-px px-2 bg-[#D3D3D3] rounded text-sm font-alternative">
          Geral
        </span>
        <InputSearch
          className="ml-auto w-full max-w-[264px]"
          placeholder="Busque por nome"
          onChange={(e) => setSearch(e.target.value)}
          value={search}
        />
      </div>

      <ErrorBoundary
        fallback={(state) => <ErrorFallback error={state.error} />}
      >
        <OnlineUsers search={search} />
      </ErrorBoundary>
    </section>
  );
}

interface Props {
  search: string;
}
function OnlineUsers({ search }: Props) {
  const user = useUser();
  const queryClient = useQueryClient();
  const { data: users } = useSuspenseQuery({
    queryKey: ["users"],
    queryFn: async () => {
      socket.send({
        command: "message",
        identifier: socket.channels.chats(),
        data: { action: "get_users" },
      });
      const users = await Promise.race([
        new Promise<ActiveUser[]>((resolve) =>
          socket.once("online_users", resolve)
        ),
        new Promise<ActiveUser[]>((resolve) =>
          socket.once("get_users", resolve)
        ),
        new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error("Não foi possível obter os colegas online")),
            60 * 1000
          )
        ),
      ]);
      return users;
    },
    select: (data) =>
      search
        ? data.filter((it) =>
            it.name.toLowerCase().includes(search.toLowerCase())
          )
        : data,
  });
  useEffect(
    () =>
      socket.on("chat_status_changed", (newUser) =>
        queryClient.setQueryData<ActiveUser[]>(
          ["users"],
          (currentUsers = []) => {
            if (newUser.user_id === user.id) return currentUsers;

            const users = Array.from(currentUsers);
            const index = currentUsers.findIndex(
              (it) => it.id === newUser.user_id
            );
            const wasOnline = index != -1;
            if (wasOnline && newUser.chat_status === "offline") {
              users.splice(index, 1);
            }
            if (!wasOnline && newUser.chat_status === "online") {
              users.push({ ...newUser, id: newUser.user_id });
            }
            return users;
          }
        )
      ),
    [queryClient, user.id]
  );
  const callUserMutation = useMutation({
    mutationKey: ["call_user"],
    mutationFn: async (mateId: string) => {
      const { data } = await api.post(`/api/v1/chat_rooms`, {
        user2_id: mateId,
      });
      const chatRoom = data.data;
      return chatRoom;
    },
  });
  const call = useContext(CallContext);

  return (
    <ul
      className="grid grid-cols-2 gap-[10px] overflow-y-auto"
      style={{ gridAutoRows: 44, height: 44 * 3 + 2 * 10 }} // 3 rows
    >
      {users.map((user) => (
        <li
          key={user.id}
          className="rounded-[10px] bg-surface flex items-center gap-2 px-4 py-1"
        >
          <UserAvatar name={user.name} url={user.avatar_url} size={36} />
          <span className="truncate">Dr. {user.name}</span>
          <button
            type="button"
            className="ml-auto rounded bg-alternative-2 data-[pending=true]:opacity-50 text-white p-[5px]"
            disabled={callUserMutation.isPending}
            data-pending={
              callUserMutation.isPending &&
              callUserMutation.variables === user.id
            }
            onClick={async () => {
              if (call) {
                alert("Você já está em uma chamada!");
                return;
              }

              const devices = await navigator.mediaDevices.enumerateDevices();
              const hasAudio = devices.find((it) => it.kind === "audioinput");
              const hasVideo = devices.find((it) => it.kind === "videoinput");

              // Audio input is required, so we early return here
              if (!hasAudio) {
                alert(
                  "É necessário ter um dispositivo de áudio conectado para participar da chamada!"
                );
                return;
              }

              // Video input is recommended but not required
              if (!hasVideo) {
                alert(
                  "É recomendado ter um dispositivo de vídeo conectado para participar da chamada."
                );
              }

              callUserMutation.mutate(user.id);
            }}
          >
            <MdPhone size={20} />
          </button>
        </li>
      ))}
    </ul>
  );
}
interface ActiveUser {
  id: string;
  name: string;
  chat_status: string;
  avatar_url: string;
}
